import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "../../styles/CompanyManagement/CompanyManagement.css";
import { CompanyDetails, CustomerBase, OffersBenefits, Competitors, Integration, KnowledgeBase } from "../../components/CompanyManagement";
import axiosInstance from "../../utils/api";
import { useCompany } from "../../context/CompanyContext";

const CompanyManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId, setCompanyId } = useCompany();

  // Get active tab from URL query parameter, default to 'company-details'
  const getActiveTabFromParams = () => {
    const tab = searchParams.get('tab');
    const validTabs = ['company-details', 'customer-base', 'offers-benefits', 'competitors', 'integration', 'knowledge-base'];
    return validTabs.includes(tab) ? tab : 'company-details';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromParams());

  // Restore tab from sessionStorage when returning from OAuth redirect
  useEffect(() => {
    const storedUrl = sessionStorage.getItem('companyManagementReturnTab');
    if (storedUrl) {
      try {
        const url = new URL(storedUrl);
        const tab = url.searchParams.get('tab');
        const validTabs = ['company-details', 'customer-base', 'offers-benefits', 'competitors', 'integration', 'knowledge-base'];
        if (validTabs.includes(tab)) {
          setSearchParams({ tab }, { replace: true });
        }
        sessionStorage.removeItem('companyManagementReturnTab');
      } catch (e) {
        // If URL parsing fails, just clear the stored value
        sessionStorage.removeItem('companyManagementReturnTab');
      }
    }
  }, [setSearchParams]);

  // Sync activeTab with URL query parameter when location changes (e.g., coming back from OAuth)
  useEffect(() => {
    const tab = searchParams.get('tab');
    const validTabs = ['company-details', 'customer-base', 'offers-benefits', 'competitors', 'integration', 'knowledge-base'];
    const tabFromParams = validTabs.includes(tab) ? tab : 'company-details';
    setActiveTab(tabFromParams);
  }, [searchParams]);

  useEffect(() => {
    if (!companyId) {
      axiosInstance.get(`/api/v1/companies`).then(res => {
        const companies = res.data;
        if (companies && companies.length > 0) {
          setCompanyId(companies[0].id);
        }
      });
    }
  }, [companyId, setCompanyId]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Update URL query parameter without page reload
    setSearchParams({ tab: tabId }, { replace: true });
  };

  const tabs = [
    { id: "company-details", label: "Company details", component: <CompanyDetails companyId={companyId} /> },
    { id: "customer-base", label: "Customer base", component: <CustomerBase companyId={companyId} /> },
    { id: "offers-benefits", label: "Offers & Benefits", component: <OffersBenefits companyId={companyId} /> },
    { id: "competitors", label: "Competitors", component: <Competitors companyId={companyId} /> },
    { id: "integration", label: "Integration", component: <Integration companyId={companyId} /> },
    { id: "knowledge-base", label: "Knowledge Base", component: <KnowledgeBase companyId={companyId} /> },
  ];

  return (
    <div className="company-management">
      <h1>Company Management</h1>

      <div className="company-management-header">
        <div className="tabs rounded">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab rounded ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        {tabs.find((tab) => tab.id === activeTab)?.component}
      </div>
    </div>
  );
};

export default CompanyManagement;
